"use client";

import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Action } from "../../action";
import { Icon, type IconKind } from "../../icon";
import { HStack, VStack } from "../../layout/stack";
import { Pulse } from "../../loaders/pulse";
import { Pill } from "../../pill";
import { BaseToolMessage } from "./base";
import type { BaseToolMessageProps } from "./types";

// Constants
const CURRENT_DATE = new Date().toISOString().split("T")[0]; // Current date in YYYY-MM-DD format

// Types
interface EmailToolStepProps {
	text: string;
	isActive?: boolean;
	isDone?: boolean;
}

// Helper functions
function getSourceIcon(source: string): IconKind {
	const lowerSource = source.toLowerCase();
	if (lowerSource.includes("email")) return "email";
	if (lowerSource.includes("slack")) return "slack";
	if (lowerSource.includes("notion")) return "notion";
	return "search";
}

function calculateDaysDifference(startDate: string, endDate: string): number {
	const start = new Date(startDate);
	const end = new Date(endDate);
	return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

function getTimeRangeText(startDate: string, endDate: string) {
	// If end date is today
	if (endDate === CURRENT_DATE) {
		const diffDays = calculateDaysDifference(startDate, endDate);

		if (diffDays === 1) return "since yesterday";
		if (diffDays === 7) return "from the last week";
		if (diffDays === 30 || diffDays === 31) return "from the last month";
		if (diffDays <= 31) return `from the last ${diffDays} days`;
	}

	// Default to original format
	return (
		<>
			<span className="text-gray-600 text-sm">between</span>
			<Pill className="text-gray-800 bg-black/5 lowercase mx-1">
				{startDate}
			</Pill>
			<span className="text-gray-600 text-sm">and</span>
			<Pill className="text-gray-800 bg-black/5 lowercase mx-1">{endDate}</Pill>
		</>
	);
}

function getSinceText(date: string) {
	const diffDays = calculateDaysDifference(date, CURRENT_DATE);

	if (diffDays === 1) return "since yesterday";
	if (diffDays === 7) return "from the last week";
	if (diffDays === 30 || diffDays === 31) return "from the last month";
	if (diffDays <= 31) return `from the last ${diffDays} days`;

	// Default to original format
	return (
		<>
			<span className="text-gray-600 text-sm">since</span>
			<Pill className="text-gray-800 bg-black/5 lowercase mx-1">{date}</Pill>
		</>
	);
}

// Components
function EmailToolStepIndicator({
	isActive,
	isDone,
}: Omit<EmailToolStepProps, "text">): React.ReactElement {
	if (isActive) return <Pulse />;
	if (isDone) return <Icon kind="done-filled" size="sm" />;
	return <div className="h-icon-sm w-icon-sm" />;
}

function SourcePill({ source }: { source: string }): React.ReactElement {
	return (
		<Pill className="text-gray-800 bg-black/5 capitalize inline-flex items-center pr-[0.625rem] text-sm">
			<span className="scale-[0.8] flex items-center">
				<Icon kind={getSourceIcon(source)} size="xs" className="mr-1" />
			</span>
			<span>{source}</span>
		</Pill>
	);
}

function SearchQueryDisplay({ query }: { query: string }): React.ReactElement {
	return (
		<Pill className="text-gray-800 bg-black/5 mx-0.5 normal-case text-sm">
			{query}
		</Pill>
	);
}

function DateRangeDisplay({
	startDate,
	endDate,
}: {
	startDate: string;
	endDate: string;
}): React.ReactElement {
	return (
		<span className="flex items-center">
			{endDate === CURRENT_DATE ? (
				<span className="text-gray-600 text-sm">
					{getTimeRangeText(startDate, endDate)}
				</span>
			) : (
				getTimeRangeText(startDate, endDate)
			)}
		</span>
	);
}

function SinceDateDisplay({ date }: { date: string }): React.ReactElement {
	return (
		<span className="flex items-center">
			<span className="text-gray-600 text-sm">{getSinceText(date)}</span>
		</span>
	);
}

export function EmailToolStep({
	text,
	isActive,
	isDone,
}: EmailToolStepProps): React.ReactElement {
	const variants = {
		hidden: { opacity: 0, x: "-12px" },
		show: { opacity: 1, x: 0 },
	};

	const [showDateSection, setShowDateSection] = useState(true);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!containerRef.current) return;

		const checkAvailableSpace = () => {
			const containerWidth = containerRef.current?.clientWidth || 0;
			const contentElements =
				containerRef.current?.querySelectorAll(".content-section") || [];

			if (contentElements.length > 0) {
				const contentElement = contentElements[0] as HTMLElement;
				const contentWidth = contentElement.offsetWidth;

				// If content takes up more than 70% of the container, hide date section
				const threshold = 0.7;
				setShowDateSection(contentWidth / containerWidth < threshold);
			}
		};

		// Initial check
		checkAvailableSpace();

		// Set up resize observer to check on resize
		const resizeObserver = new ResizeObserver(checkAvailableSpace);
		resizeObserver.observe(containerRef.current);

		return () => {
			resizeObserver.disconnect();
		};
	}, [text]);

	// Extract and format search information
	const renderFormattedText = () => {
		// Check if the text is a search query
		if (
			!text.includes("Searching for:") &&
			!text.startsWith("all emails from")
		) {
			return <span className="text-gray-600 text-sm">{text}</span>;
		}

		// Extract search query
		let remainingText = "";
		let queryType = "text";

		if (text.includes("Searching for:")) {
			const parts = text.split("Searching for:");
			remainingText = parts[1].trim();
			queryType = "search";
		} else if (text.startsWith("all emails from")) {
			remainingText = text;
			queryType = "from";
		}

		// Extract email addresses for from/to patterns
		const fromEmailMatch = remainingText.match(
			/from\s+([^\s]+@[^\s]+\.[^\s]+)(?:\s|$)/,
		);
		const toEmailMatch = remainingText.match(
			/to\s+([^\s]+@[^\s]+\.[^\s]+)(?:\s|$)/,
		);

		// Handle "all emails from" pattern
		const allEmailsFromMatch = text.match(
			/all emails from\s+([^\s]+@[^\s]+\.[^\s]+)(?:\s|$)/,
		);

		// Determine if this is an email search query
		const isEmailSearch = fromEmailMatch || toEmailMatch || allEmailsFromMatch;

		// Avoid duplication between allEmailsFromMatch and fromEmailMatch
		// If we have both, prefer allEmailsFromMatch and ignore fromEmailMatch
		const shouldShowFromEmail = fromEmailMatch && !allEmailsFromMatch;

		// Extract quoted content
		const quoteMatches = remainingText.match(/"([^"]*)"/g);

		// Extract dates
		const betweenMatch = remainingText.match(
			/between\s([\d-]+)\sand\s([\d-]+)/,
		);
		const sinceMatch = remainingText.match(/since\s([\d-]+)/);

		// Extract source (in emails, in Slack, in Notion)
		const sourceMatch = remainingText.match(/\sin\s([^\s]+)(?:\s|$)/);
		let source = sourceMatch ? sourceMatch[1] : "";

		// If no direct match, check for other indicators
		if (!source) {
			if (remainingText.includes("in emails")) source = "emails";
			else if (remainingText.includes("in Slack")) source = "Slack";
			else if (remainingText.includes("in Notion")) source = "Notion";
		}

		// Default to emails if no source specified but we have email addresses
		if (!source && (fromEmailMatch || toEmailMatch || allEmailsFromMatch)) {
			source = "emails";
		} else if (!source) {
			// If still no source, default to emails for all searches
			source = "emails";
		}

		// Always show source
		const shouldShowSource = !!source;

		return (
			<div className="flex flex-wrap items-center w-full gap-0.5">
				<div className="flex flex-wrap items-center content-section gap-0.5">
					{(queryType === "search" || queryType === "from") && (
						<span className="text-gray-600 mr-0.5 text-sm">
							{isDone ? "Searched" : "Searching"}
						</span>
					)}

					{allEmailsFromMatch && (
						<>
							<Pill className="text-gray-800 bg-black/5 mx-0.5 normal-case text-sm">
								from: {allEmailsFromMatch[1]}
							</Pill>
						</>
					)}

					{/* Only show quotes if we don't have specific email patterns */}
					{quoteMatches &&
						!isEmailSearch &&
						quoteMatches.map((quote, i) => (
							<SearchQueryDisplay key={i} query={quote} />
						))}

					{shouldShowFromEmail && (
						<>
							<Pill className="text-gray-800 bg-black/5 mx-0.5 normal-case text-sm">
								from: {fromEmailMatch[1]}
							</Pill>
						</>
					)}

					{toEmailMatch && (
						<>
							<Pill className="text-gray-800 bg-black/5 mx-0.5 normal-case text-sm">
								to: {toEmailMatch[1]}
							</Pill>
						</>
					)}

					{shouldShowSource && (
						<>
							<span className="text-gray-600 mx-0.5 text-sm">in</span>
							<SourcePill source={source} />
						</>
					)}
				</div>

				{showDateSection && (betweenMatch || sinceMatch) && (
					<div className="flex flex-wrap items-center ml-auto gap-0.5">
						{betweenMatch && (
							<DateRangeDisplay
								startDate={betweenMatch[1]}
								endDate={betweenMatch[2]}
							/>
						)}

						{sinceMatch && <SinceDateDisplay date={sinceMatch[1]} />}
					</div>
				)}
			</div>
		);
	};

	return (
		<HStack asChild className="items-center gap-sm pr-3 w-full">
			<motion.div
				animate="show"
				initial="hidden"
				variants={variants}
				className="flex items-center w-full"
			>
				<div className="h-[1.5rem] flex items-center">
					<EmailToolStepIndicator isActive={isActive} isDone={isDone} />
				</div>
				<div
					ref={containerRef}
					className={`${!isActive && !isDone ? "opacity-50" : ""} w-full`}
				>
					{renderFormattedText()}
				</div>
			</motion.div>
		</HStack>
	);
}

// EmailToolMessage for all email operations
export interface EmailToolMessageProps extends BaseToolMessageProps {
	text: string;
	icon: IconKind;
	isLoading?: boolean;
	isDone?: boolean;
	children?: ReactNode;
	stepCount?: number;
}

export function EmailToolMessage(
	props: EmailToolMessageProps,
): React.ReactElement {
	const { children, stepCount, ...rest } = props;
	const [expanded, setExpanded] = useState(!props.isDone);

	const toggleExpanded = (): void => {
		setExpanded((prev: boolean) => !prev);
	};

	const variants = {
		closed: { height: 0 },
		open: { height: "auto", transition: { staggerChildren: 2 } },
	};

	useEffect(() => {
		if (props.isDone) {
			setExpanded(false);
		}
	}, [props.isDone]);

	const actions = [];

	if (Number(stepCount) > 0) {
		actions.push(
			<Action
				icon="list"
				key="details"
				onClick={toggleExpanded}
				tooltip="Show Details"
				className="text-surface-icon-emphasized"
			/>,
		);
	}

	return (
		<BaseToolMessage
			className="bg-message-emailrelated-background"
			actions={Number(stepCount) > 0 ? actions : undefined}
			{...rest}
		>
			{Number(stepCount) > 0 && expanded && children ? (
				<VStack asChild className="my-md gap-sm pl-lg">
					<motion.div animate="open" variants={variants}>
						{children}
					</motion.div>
				</VStack>
			) : null}
		</BaseToolMessage>
	);
}
